/**
 * GeneratedContractSection
 * ------------------------
 * Sezione read-only che rappresenta il punto di collegamento permanente
 * tra un'Offerta di Subappalto CONTRATTUALIZZATA e il Contratto di Subappalto
 * generato a partire da essa.
 *
 * Progettata per essere estesa negli step successivi (Extra, SAL, fatture
 * passive, avanzamento economico) SENZA refactoring:
 * - Ogni "dimensione" del contratto è renderizzata come un blocco `<Block>`
 *   con etichetta e un array di righe `<Row>` (label + value).
 * - Nuove dimensioni si aggiungono semplicemente accodando un `<Block>`.
 * - I dati provengono ESCLUSIVAMENTE dal contratto collegato via
 *   `offer.contractualized_subcontract_id` — non si duplica alcun campo
 *   che appartiene al contratto.
 *
 * Feature flag: nessuno. Endpoint utilizzati: solo GET /api/subcontracts/{id}
 * (già esistente).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { ExternalLink, FileSignature, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Dedup fetch cache (module-scope).
// Motivo del doppio GET osservato in iter #107: React.StrictMode (index.js:8) in
// dev double-invoca gli useEffect. Un semplice `cancelled` flag ferma la SetState
// ma NON annulla la seconda richiesta HTTP.
// Soluzione senza librerie:
//   • `inflight`: Map<id, Promise> per condividere la stessa Promise fra i mount
//     concorrenti (StrictMode) → 1 sola richiesta HTTP.
//   • `cache`:    Map<id, {data, ts}> con TTL breve (30s) per riaperture rapide
//     dello stesso dettaglio → 0 richieste HTTP se cache fresca.
// Nota: il fetch è sempre cancellabile via AbortController (aborta la HTTP call
// se il componente si smonta prima della risoluzione).
// ─────────────────────────────────────────────────────────────────────────────
const _subcontractCache = new Map();      // id → { data, ts }
const _subcontractInflight = new Map();   // id → Promise
const _CACHE_TTL_MS = 30_000;

const _isFresh = (entry) => entry && (Date.now() - entry.ts) < _CACHE_TTL_MS;

const fetchSubcontract = (id, signal) => {
  const cached = _subcontractCache.get(id);
  if (_isFresh(cached)) return Promise.resolve(cached.data);

  const running = _subcontractInflight.get(id);
  if (running) return running;

  const token = localStorage.getItem('token');
  const p = axios
    .get(`${API}/api/subcontracts/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal,
    })
    .then((r) => {
      _subcontractCache.set(id, { data: r.data, ts: Date.now() });
      return r.data;
    })
    .finally(() => {
      _subcontractInflight.delete(id);
    });
  _subcontractInflight.set(id, p);
  return p;
};

// Utility esportata per invalidazione manuale (es. dopo update contratto).
export const invalidateSubcontractCache = (id) => {
  if (id) _subcontractCache.delete(id);
  else _subcontractCache.clear();
};

// Stati del Contratto di Subappalto → label + palette coerente con la tab Subappalti.
const CONTRACT_STATUS_LABELS = {
  bozza: 'Bozza',
  pronto_per_firma: 'Pronto per firma',
  firmato: 'Firmato',
  annullato: 'Annullato',
  risolto: 'Risolto',
  chiuso: 'Chiuso',
};
const CONTRACT_STATUS_COLORS = {
  bozza: 'bg-slate-100 text-slate-700 border-slate-300',
  pronto_per_firma: 'bg-amber-100 text-amber-800 border-amber-300',
  firmato: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  annullato: 'bg-zinc-200 text-zinc-800 border-zinc-400',
  risolto: 'bg-violet-100 text-violet-800 border-violet-300',
  chiuso: 'bg-teal-100 text-teal-800 border-teal-400',
};

// -------- Formatters --------
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
};
const fmtEUR = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);
};

// -------- Building blocks --------
/**
 * Row: singola coppia label/valore all'interno di un Block.
 * Se `value` è null/undefined, mostra "—".
 */
const Row = ({ label, value, testid, mono = false }) => (
  <div className="flex justify-between items-baseline gap-3 py-1.5 border-b border-slate-200/60 last:border-0">
    <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
    <span
      className={`text-sm text-slate-900 text-right ${mono ? 'font-mono' : 'font-medium'}`}
      data-testid={testid}
    >
      {value ?? '—'}
    </span>
  </div>
);

/**
 * Block: raggruppa 1..N Row sotto un titolo. È l'unità estendibile:
 * per aggiungere una nuova dimensione (Extra, SAL, fatture...) basta
 * accodare un nuovo <Block>.
 */
const Block = ({ title, children, testid, action = null }) => (
  <section
    className="rounded-lg border border-slate-200 bg-white/70 backdrop-blur-sm p-3"
    data-testid={testid}
  >
    <header className="flex items-center justify-between mb-2">
      <h4 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">{title}</h4>
      {action}
    </header>
    <div className="space-y-0">{children}</div>
  </section>
);

// -------- Main component --------
const GeneratedContractSection = ({ offer }) => {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const contractId = offer?.contractualized_subcontract_id;

  useEffect(() => {
    if (!contractId) { setLoading(false); return; }
    const ctrl = new AbortController();
    let cancelled = false;
    // Se la cache è fresca, evita completamente l'effetto async → nessun HTTP call.
    const cached = _subcontractCache.get(contractId);
    if (_isFresh(cached)) {
      setContract(cached.data);
      setError(null);
      setLoading(false);
      return () => {};
    }
    setLoading(true);
    setError(null);
    fetchSubcontract(contractId, ctrl.signal)
      .then((data) => { if (!cancelled) setContract(data); })
      .catch((err) => {
        if (cancelled || axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError(err?.response?.data?.detail?.message || err?.message || 'Impossibile caricare il contratto collegato');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      // Non abortiamo la richiesta condivisa: altre istanze montate potrebbero attenderla.
      // AbortController resta a disposizione per usi futuri (es. cancel esplicito).
    };
  }, [contractId]);

  const openContract = () => {
    if (!contractId) return;
    window.open(`/subcontracts/${contractId}`, '_blank', 'noopener,noreferrer');
  };

  // ------ Stati particolari ------
  if (!contractId) return null;

  return (
    <div
      className="mt-4 rounded-xl border border-teal-300/70 bg-gradient-to-br from-teal-50/60 to-white p-4"
      data-testid="generated-contract-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-md bg-teal-100 text-teal-800">
            <FileSignature className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Contratto generato</h3>
            <p className="text-xs text-slate-500">
              Origine: Offerta {offer?.offer_number} · Rev. {offer?.offer_revision_number}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={openContract}
          disabled={loading || !!error}
          className="bg-[#FF7819] hover:bg-[#e56811] text-white"
          data-testid="generated-contract-open-btn"
        >
          <ExternalLink className="w-4 h-4 mr-1" /> Apri Contratto
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="generated-contract-loading">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      )}

      {/* Errore */}
      {!loading && error && (
        <div
          className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="generated-contract-error"
        >
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Impossibile caricare il contratto collegato: {error}</span>
        </div>
      )}

      {/* Contenuto — griglia di Blocchi estendibile */}
      {!loading && !error && contract && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Blocco 1 · Identificazione contratto */}
          <Block title="Identificazione" testid="generated-contract-block-identification">
            <Row
              label="Numero"
              value={contract.subcontract_number || '—'}
              testid="generated-contract-number"
              mono
            />
            <Row
              label="Stato"
              value={
                <Badge
                  className={CONTRACT_STATUS_COLORS[contract.status] || 'bg-slate-100 text-slate-700 border-slate-300'}
                  data-testid="generated-contract-status"
                >
                  {CONTRACT_STATUS_LABELS[contract.status] || contract.status}
                </Badge>
              }
            />
            <Row
              label="Contrattualizzata il"
              value={fmtDate(offer?.contractualized_at)}
              testid="generated-contract-contractualized-at"
            />
          </Block>

          {/* Blocco 2 · Dati economici (oggi = solo importo contrattuale) */}
          <Block title="Dati economici" testid="generated-contract-block-economics">
            <Row
              label="Importo contrattuale"
              value={fmtEUR(contract.total_amount_net)}
              testid="generated-contract-amount"
            />
            <Row
              label="Origine · Revisione Offerta"
              value={
                contract.source_offer_number
                  ? `${contract.source_offer_number} · Rev. ${contract.source_offer_revision ?? '—'}`
                  : `Rev. ${offer?.offer_revision_number ?? '—'}`
              }
              testid="generated-contract-source-revision"
              mono
            />
          </Block>

          {/*
            Blocchi futuri (progettati come slot vuoti, NON renderizzati oggi):
            - <Block title="Extra" ...>            → num. extra, importo extra, stato
            - <Block title="SAL" ...>               → num. SAL, importo maturato
            - <Block title="Fatture passive" ...>   → num. fatture, importo, saldo
            - <Block title="Avanzamento economico" ...> → % completamento, delta budget
            Aggiungerli in step successivi = 1 nuovo <Block> senza modificare
            questa struttura. Nessun refactoring richiesto.
          */}
        </div>
      )}
    </div>
  );
};

export default GeneratedContractSection;
